import {
  BadgePercent,
  Bus,
  CheckCircle2,
  Clock3,
  FileCheck2,
  FileText,
  GripVertical,
  Hotel,
  MapPinned,
  PlaneTakeoff,
  Route,
  Sparkles,
} from "lucide-react";
import { motion } from "motion/react";
import type { CSSProperties } from "react";
import { useEffect, useRef, useState } from "react";
import { MotionSection } from "./HomepagePrimitives";

type FeatureVariant = "pipeline" | "itinerary" | "operations" | "finance";

type FeatureRow = {
  tagline: string;
  headline: string;
  text: string;
  imageOnLeft: boolean;
  variant: FeatureVariant;
};

type CountUpTextProps = {
  end: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  padStart?: number;
  className?: string;
};

const featureFlyIn = (direction: "left" | "right") => ({
  hidden: {
    opacity: 0,
    x: direction === "left" ? -88 : 88,
    scale: 0.97,
    filter: "blur(10px)",
    transition: {
      duration: 0.24,
      ease: [0.22, 1, 0.36, 1] as const,
    },
  },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1,
    filter: "blur(0px)",
    transition: {
      type: "spring" as const,
      stiffness: 320,
      damping: 31,
    },
  },
});

/**
 * Counts numeric mockup values up when they first enter the viewport.
 * @param props Count-up value formatting props
 * @returns Animated numeric text
 */
const CountUpText = ({
  end,
  prefix = "",
  suffix = "",
  decimals = 0,
  padStart = 0,
  className = "",
}: CountUpTextProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const hasAnimatedRef = useRef(false);
  const [value, setValue] = useState(0);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion) {
      setValue(end);
      return undefined;
    }

    let animationFrame = 0;
    let startTime = 0;
    const duration = 980;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      setValue(end * eased);

      if (progress < 1) {
        animationFrame = window.requestAnimationFrame(animate);
      }
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || hasAnimatedRef.current) return;
        hasAnimatedRef.current = true;
        animationFrame = window.requestAnimationFrame(animate);
        observer.disconnect();
      },
      { threshold: 0.45 },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(animationFrame);
    };
  }, [end]);

  const formattedValue =
    decimals > 0 ? value.toFixed(decimals) : Math.round(value).toLocaleString("en-US").padStart(padStart, "0");

  return (
    <span ref={ref} className={className}>
      {prefix}
      {formattedValue}
      {suffix}
    </span>
  );
};

/**
 * Animated feature surface matching the associated content row.
 * @param props Surface variant props
 * @returns UI showcase panel
 */
const FeatureSurface = ({ variant }: { variant: FeatureVariant }) => {
  if (variant === "pipeline") {
    return (
      <div className="grid gap-4">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-[#131B2A]/44">Round-robin queue</p>
              <p className="mt-2 text-3xl font-semibold tracking-normal text-[#131B2A]">
                <CountUpText end={86} />
              </p>
            </div>
            <div className="tb-ops-oscillate rounded-lg border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-semibold text-[#131B2A]">
              Auto assigned
            </div>
          </div>
          <div className="mt-5 space-y-3">
            {[
              { label: "Dubai luxury family", width: "w-[82%]" },
              { label: "Bhutan retreat group", width: "w-[60%]" },
              { label: "Bali honeymoon", width: "w-[46%]" },
            ].map((item, index) => (
              <div
                key={item.label}
                className="tb-ops-oscillate rounded-lg border border-gray-200 bg-gray-50 p-3"
                style={{ "--tb-loop-delay": `${index * 0.16}s` } as CSSProperties}
              >
                <div className="flex items-center justify-between text-sm font-medium text-[#131B2A]">
                  <span>{item.label}</span>
                  <span className="flex items-center gap-1.5 text-xs text-[#131B2A]/45">
                    <span className="tb-loop-pulse h-1.5 w-1.5 rounded-full bg-(--brand-primary)" />
                    Live
                  </span>
                </div>
                <div className="mt-3 h-2 rounded-full bg-gray-200">
                  <div
                    className={`tb-progress-breathe h-2 rounded-full bg-[#131B2A] ${item.width}`}
                    style={{ "--tb-loop-delay": `${index * 0.12}s` } as CSSProperties}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-[#131B2A] p-4 text-white">
          <p className="text-xs uppercase tracking-[0.16em] text-white/48">Lead sources</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            {["Meta ads", "Google search", "Referral forms"].map((item, index) => (
              <div
                key={item}
                className="tb-node-float rounded-lg border border-white/10 bg-white/5 px-3 py-3 text-sm"
                style={{ "--tb-loop-delay": `${index * 0.16}s` } as CSSProperties}
              >
                {item}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (variant === "itinerary") {
    return (
      <div className="grid min-w-0 gap-4 2xl:grid-cols-[1.18fr_0.82fr]">
        <div className="min-w-0 rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-[#131B2A]/44">10-second itinerary builder</p>
              <p className="mt-2 text-sm font-semibold tracking-normal text-[#131B2A]">Sri Lanka family escape</p>
            </div>
            <div className="tb-ops-oscillate flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-semibold text-[#131B2A]">
              <Clock3 className="h-3.5 w-3.5 text-(--brand-primary)" />
              00:
              <CountUpText end={47} padStart={2} />
            </div>
          </div>

          <div className="mt-5 grid min-w-0 gap-3 sm:grid-cols-[0.9fr_1.1fr]">
            <div className="min-w-0 rounded-lg border border-gray-200 bg-[#FBFBFB] p-3">
              <div className="flex items-center gap-2 text-xs font-semibold text-[#131B2A]/62">
                <Sparkles className="tb-loop-icon h-4 w-4 text-(--brand-primary)" />
                Template library
              </div>
              <div className="mt-3 space-y-2">
                {[
                  { label: "Family classic", active: true },
                  { label: "Luxury pace", active: false },
                  { label: "Adventure add-ons", active: false },
                ].map((item, index) => (
                  <div
                    key={item.label}
                    className={`tb-node-float rounded-md border px-3 py-2 text-xs font-semibold ${
                      item.active
                        ? "border-(--brand-primary)/50 bg-(--brand-primary)/10 text-[#131B2A]"
                        : "border-gray-200 bg-white text-[#131B2A]/62"
                    }`}
                    style={{ "--tb-loop-delay": `${index * 0.14}s` } as CSSProperties}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            </div>

            <div className="min-w-0 rounded-lg border border-gray-200 bg-[#FBFBFB] p-3">
              <div className="flex items-center justify-between text-xs font-semibold text-[#131B2A]/62">
                <span>Services assembled</span>
                <span className="tb-loop-pulse text-(--brand-primary)">Live build</span>
              </div>
              <div className="mt-3 space-y-2">
                {[
                  { label: "Hotel nights", icon: Hotel, value: 4 },
                  { label: "Transfers", icon: Bus, value: 6 },
                  { label: "Activities", icon: Route, value: 9 },
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.label}
                      className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 rounded-md border border-gray-200 bg-white px-2.5 py-2"
                    >
                      <Icon className="tb-loop-icon h-4 w-4 text-(--brand-primary)" strokeWidth={1.8} />
                      <span className="truncate text-xs font-semibold text-[#131B2A]">{item.label}</span>
                      <span
                        className="tb-ops-oscillate rounded border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11px] font-semibold text-[#131B2A]/70"
                        style={{ "--tb-loop-delay": `${index * 0.13}s` } as CSSProperties}
                      >
                        <CountUpText end={item.value} />
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mt-4 space-y-3">
            {[
              {
                day: "D1",
                title: "Airport arrival, Colombo stay",
                meta: "Hotel + transfer",
                icon: PlaneTakeoff,
              },
              {
                day: "D2",
                title: "Kandy rail, temple visit",
                meta: "Guide + activity",
                icon: MapPinned,
              },
              {
                day: "D3",
                title: "Tea estate and south coast",
                meta: "Route + beach hotel",
                icon: Hotel,
              },
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="group/day grid min-w-0 grid-cols-[auto_auto_minmax(0,1fr)_auto] items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 transition-colors duration-150 hover:border-(--brand-primary)/60"
                >
                  <GripVertical className="h-4 w-4 text-gray-300 transition-colors duration-150 group-hover/day:text-(--brand-primary)" />
                  <div
                    className="tb-node-float flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white text-xs font-semibold text-[#131B2A]"
                    style={{ "--tb-loop-delay": `${index * 0.14}s` } as CSSProperties}
                  >
                    {item.day}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Icon className="tb-loop-icon h-4 w-4 shrink-0 text-(--brand-primary)" strokeWidth={1.8} />
                      <p className="truncate text-sm font-semibold text-[#131B2A]">{item.title}</p>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-xs font-medium text-[#131B2A]/48">{item.meta}</span>
                      <span
                        className="tb-flow-line h-px min-w-12 flex-1 bg-gray-200"
                        style={{ "--tb-loop-delay": `${index * 0.18}s` } as CSSProperties}
                      />
                    </div>
                  </div>
                  <CheckCircle2 className="tb-loop-pulse h-4 w-4 text-(--brand-primary)" />
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid min-w-0 gap-4">
          <div className="min-w-0 rounded-xl border border-gray-200 bg-[#131B2A] p-4 text-white">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs uppercase tracking-[0.16em] text-white/48">Pricing engine</p>
              <BadgePercent className="tb-loop-icon h-5 w-5 text-(--brand-primary)" />
            </div>
            <p className="mt-4 text-4xl font-semibold tracking-normal">
              <CountUpText end={18} suffix="%" />
            </p>
            <p className="mt-1 text-xs text-white/64">Markup applied automatically</p>
            <div className="tb-flow-line mt-5 h-px bg-white/12" />
            <div className="mt-4 grid min-w-0 grid-cols-2 gap-3">
              {[
                { label: "Cost", value: 3.42 },
                { label: "Sell", value: 4.04 },
              ].map((item, index) => (
                <div
                  key={item.label}
                  className="tb-ops-oscillate min-w-0 rounded-lg border border-white/10 bg-white/[0.04] p-3"
                  style={{ "--tb-loop-delay": `${index * 0.16}s` } as CSSProperties}
                >
                  <p className="text-[11px] text-white/48">{item.label}</p>
                  <p className="mt-1 truncate text-sm font-semibold">
                    <CountUpText end={item.value} prefix="₹" suffix="L" decimals={2} />
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="min-w-0 rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs font-medium text-[#131B2A]/44">Proposal output</p>
              <div className="tb-ops-oscillate flex items-center gap-1.5 rounded-lg border border-(--brand-primary)/40 bg-(--brand-primary)/10 px-2.5 py-1 text-xs font-semibold text-[#131B2A]">
                <FileCheck2 className="h-3.5 w-3.5" />
                Ready
              </div>
            </div>
            <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#131B2A] text-white">
                  <FileText className="tb-loop-icon h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="h-2.5 w-28 rounded-full bg-[#131B2A]" />
                  <div className="mt-2 h-1.5 w-full rounded-full bg-gray-200" />
                </div>
              </div>
              <div className="mt-5 space-y-2">
                {["Branded PDF", "Client pricing", "Supplier notes"].map((item, index) => (
                  <div key={item} className="flex items-center gap-2 text-xs font-semibold text-[#131B2A]/64">
                    <span
                      className="tb-loop-pulse h-2 w-2 rounded-full bg-(--brand-primary)"
                      style={{ "--tb-loop-delay": `${index * 0.15}s` } as CSSProperties}
                    />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (variant === "operations") {
    return (
      <div className="grid gap-4">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-[#131B2A]/44">Movement chart</p>
              <p className="mt-2 text-sm font-semibold tracking-normal text-[#131B2A]">Departure execution board</p>
            </div>
            <div className="tb-ops-oscillate rounded-lg border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-semibold text-[#131B2A]">
              <CountUpText end={8} /> trips active
            </div>
          </div>
          <div className="mt-5 space-y-3">
            {[
              "Airport pickups assigned",
              "Hotel vouchers issued",
              "Guide confirmations pending",
              "Transport manifests shared",
            ].map((item, index) => (
              <div
                key={item}
                className="tb-ops-oscillate flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3"
                style={{ "--tb-loop-delay": `${index * 0.13}s` } as CSSProperties}
              >
                <div className="flex items-center gap-3">
                  <div className="tb-node-float flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white text-xs font-semibold text-[#131B2A]">
                    0{index + 1}
                  </div>
                  <span className="text-sm font-medium text-[#131B2A]">{item}</span>
                </div>
                <div className="tb-loop-pulse h-2.5 w-2.5 rounded-full bg-(--brand-primary)" />
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-[#131B2A] p-4 text-white">
          <p className="text-xs uppercase tracking-[0.16em] text-white/48">Booking states</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            {[
              { label: "Confirmed", value: "42" },
              { label: "Voucher sent", value: "35" },
              { label: "Supplier hold", value: "07" },
            ].map((item, index) => (
              <div
                key={item.label}
                className="tb-node-float rounded-lg border border-white/10 bg-white/5 px-3 py-4"
                style={{ "--tb-loop-delay": `${index * 0.16}s` } as CSSProperties}
              >
                <p className="text-xs text-white/56">{item.label}</p>
                <p className="mt-2 text-2xl font-semibold tracking-normal">
                  <CountUpText end={Number(item.value)} padStart={item.value.length} />
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid min-w-0 gap-4 2xl:grid-cols-[1.16fr_0.84fr]">
      <div className="min-w-0 rounded-xl border border-gray-200 bg-[#131B2A] p-4 text-white">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.16em] text-white/48">Collection command</p>
            <p className="mt-3 text-4xl font-semibold tracking-normal">
              <CountUpText end={18.43} prefix="₹" suffix="L" decimals={2} />
            </p>
            <p className="mt-1 text-xs text-white/58">Receivable pipeline</p>
          </div>
          <div className="tb-ops-oscillate rounded-lg border border-(--brand-primary)/30 bg-(--brand-primary)/10 px-3 py-2 text-right">
            <p className="text-[11px] text-white/48">Collection rate</p>
            <p className="mt-1 text-xl font-semibold text-(--brand-primary)">
              <CountUpText end={63} suffix="%" />
            </p>
          </div>
        </div>

        <div className="mt-5 space-y-4">
          {[
            { label: "Client receipts", width: "w-[76%]", amount: 11.82 },
            { label: "Supplier payouts", width: "w-[58%]", amount: 6.4 },
            { label: "Upcoming dues", width: "w-[36%]", amount: 2.18 },
          ].map((item, index) => (
            <div key={item.label}>
              <div className="flex items-center justify-between text-xs text-white/64">
                <span>{item.label}</span>
                <span>
                  <CountUpText end={item.amount} prefix="₹" suffix="L" decimals={2} />
                </span>
              </div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/10">
                <div
                  className={`tb-progress-breathe h-2 rounded-full bg-(--brand-primary) ${item.width}`}
                  style={{ "--tb-loop-delay": `${index * 0.15}s` } as CSSProperties}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 grid grid-cols-3 gap-3">
          {[
            ["Paid", 42],
            ["Due", 18],
            ["Overdue", 3],
          ].map(([label, value], index) => (
            <div
              key={label}
              className="tb-node-float rounded-lg border border-white/10 bg-white/[0.04] p-3"
              style={{ "--tb-loop-delay": `${index * 0.14}s` } as CSSProperties}
            >
              <p className="text-[11px] text-white/48">{label}</p>
              <p className="mt-1 text-xl font-semibold">
                <CountUpText end={Number(value)} padStart={label === "Overdue" ? 2 : 0} />
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid min-w-0 gap-4">
        <div className="min-w-0 rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-medium text-[#131B2A]/44">Payment schedule</p>
              <p className="mt-1 text-sm font-semibold text-[#131B2A]">
                <CountUpText end={27} /> active ledgers
              </p>
            </div>
            <div className="tb-ops-oscillate rounded-lg border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-semibold text-[#131B2A]">
              Balanced
            </div>
          </div>

          <div className="mt-4 divide-y divide-gray-200 rounded-lg border border-gray-200 bg-[#FBFBFB]">
            {[
              { label: "Deposit received", amount: 1.2, status: "Paid" },
              { label: "Final balance", amount: 2.84, status: "Due 18 Jul" },
              { label: "Supplier advance", amount: 0.72, status: "Queued" },
            ].map((item, index) => (
              <div key={item.label} className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 p-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className="tb-loop-pulse h-2 w-2 rounded-full bg-(--brand-primary)"
                      style={{ "--tb-loop-delay": `${index * 0.12}s` } as CSSProperties}
                    />
                    <p className="truncate text-sm font-semibold text-[#131B2A]">{item.label}</p>
                  </div>
                  <p className="mt-1 text-xs font-medium text-[#131B2A]/48">{item.status}</p>
                </div>
                <p className="text-sm font-semibold text-[#131B2A]">
                  <CountUpText end={item.amount} prefix="₹" suffix="L" decimals={2} />
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="grid min-w-0 gap-4 sm:grid-cols-2">
          <div className="min-w-0 rounded-xl border border-gray-200 bg-white p-4">
            <p className="text-xs font-medium text-[#131B2A]/44">Due this week</p>
            <p className="mt-2 text-2xl font-semibold text-[#131B2A]">
              <CountUpText end={6.74} prefix="₹" suffix="L" decimals={2} />
            </p>
            <div className="mt-4 space-y-2">
              <div className="tb-flow-line h-2.5 w-full rounded-full bg-[#131B2A]" />
              <div className="tb-progress-breathe h-2 w-[74%] rounded-full bg-gray-200" />
              <div
                className="tb-progress-breathe h-2 w-[52%] rounded-full bg-gray-200"
                style={{ "--tb-loop-delay": "0.18s" } as CSSProperties}
              />
            </div>
          </div>
          <div className="min-w-0 rounded-xl border border-gray-200 bg-white p-4">
            <p className="text-xs font-medium text-[#131B2A]/44">Paid on time</p>
            <p className="mt-2 text-2xl font-semibold text-[#131B2A]">
              <CountUpText end={92} suffix="%" />
            </p>
            <div className="mt-4 flex h-[64px] items-end gap-2">
              <div className="tb-ops-oscillate h-[34px] flex-1 rounded-t-md bg-[#131B2A]/18" />
              <div
                className="tb-ops-oscillate h-[48px] flex-1 rounded-t-md bg-[#131B2A]/42"
                style={{ "--tb-loop-delay": "0.14s" } as CSSProperties}
              />
              <div
                className="tb-ops-oscillate h-[62px] flex-1 rounded-t-md bg-(--brand-primary)"
                style={{ "--tb-loop-delay": "0.28s" } as CSSProperties}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Sticky zig-zag feature walkthrough.
 * @returns Animated features section
 */
export const HomepageCoreFeaturesSection = () => {
  const features: FeatureRow[] = [
    {
      tagline: "Smart CRM",
      headline: "Automate your sales pipeline",
      text: "Auto-capture leads from Meta and Google, distribute them via round-robin, and never miss a follow-up.",
      imageOnLeft: false,
      variant: "pipeline",
    },
    {
      tagline: "Speed",
      headline: "10-second itinerary builder",
      text: "Drag-and-drop days, auto-calculate markups, and generate branded PDFs instantly.",
      imageOnLeft: true,
      variant: "itinerary",
    },
    {
      tagline: "Logistics",
      headline: "Operations control center",
      text: "Generate vouchers, manage movement charts, and track booking statuses in one click.",
      imageOnLeft: false,
      variant: "operations",
    },
    {
      tagline: "Finance",
      headline: "Automated ledgers and payments",
      text: "Track every incoming payment and outgoing supplier due date with zero math required.",
      imageOnLeft: true,
      variant: "finance",
    },
  ];

  return (
    <MotionSection
      id="core-features"
      className="border-b border-gray-200 bg-white px-4 py-24 sm:px-6"
      viewportAmount={0.01}
    >
      <div className="mx-auto max-w-7xl">
        <div className="mx-auto mb-12 max-w-5xl text-center">
          <p className="mb-4 text-sm font-semibold uppercase tracking-[0.18em] text-[#8B6A2F]">One operating layer</p>
          <h2 className="text-balance text-4xl font-semibold tracking-normal text-[#131B2A] sm:text-5xl lg:text-[3.5rem] lg:leading-tight">
            Every core travel workflow, connected from lead to collection.
          </h2>
          <motion.p
            className="mx-auto mt-6 max-w-2xl text-pretty text-lg leading-relaxed text-gray-600"
            initial={{ opacity: 0, y: 16, filter: "blur(6px)" }}
            whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            viewport={{ once: true, amount: 0.7 }}
            transition={{ type: "spring", stiffness: 360, damping: 30, delay: 0.12 }}
          >
            Replace disconnected sales, itinerary, booking, and finance tools with one premium CRM workspace designed
            around travel operations.
          </motion.p>
        </div>

        <div className="space-y-20">
          {features.map((feature, index) => {
            const hasCompactTextColumn = feature.variant === "itinerary" || feature.variant === "finance";
            const gridClassName = hasCompactTextColumn
              ? "lg:grid-cols-[minmax(0,0.68fr)_minmax(0,0.32fr)]"
              : "lg:grid-cols-[minmax(0,0.42fr)_minmax(0,0.58fr)]";

            return (
              <motion.article
                key={feature.headline}
                className="overflow-hidden rounded-xl border border-gray-200 bg-gray-50"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.26 }}
                variants={featureFlyIn(index % 2 === 0 ? "left" : "right")}
              >
                <div
                  className={`grid ${gridClassName} [&>*]:min-w-0 ${feature.imageOnLeft ? "lg:[&>*:first-child]:order-2 lg:[&>*:last-child]:order-1" : ""}`}
                >
                  <div className={`p-8 lg:p-10 ${feature.imageOnLeft ? "lg:border-l" : "lg:border-r"} border-gray-200`}>
                    <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#8B6A2F]">
                      {feature.tagline}
                    </p>
                    <h3
                      className={`mt-4 text-4xl font-semibold tracking-normal text-[#131B2A] sm:text-5xl lg:leading-tight ${
                        hasCompactTextColumn ? "max-w-[10ch] lg:text-[2.85rem]" : "max-w-[12ch] lg:text-[3.2rem]"
                      }`}
                    >
                      {feature.headline}
                    </h3>
                    <p
                      className={`mt-5 text-base leading-8 text-[#131B2A]/68 sm:text-lg ${
                        hasCompactTextColumn ? "max-w-[24ch]" : "max-w-[30ch]"
                      }`}
                    >
                      {feature.text}
                    </p>
                  </div>

                  <div className="overflow-hidden p-8 lg:p-10">
                    <FeatureSurface variant={feature.variant} />
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </MotionSection>
  );
};
